import {UseQueryOptions, useQueries} from '@tanstack/react-query';
import {
  Address,
  ApplicableContractByValueRequestModeEnum,
  EquipmentTypes,
  PaginatedContract
} from '@shipwell/backend-core-singlerequestparam-sdk';
import uniqBy from 'lodash/uniqBy';
import {CONTRACTS_QUERY_KEY} from '../queryKeys';
import {getContracts} from 'App/api/contracts/typed';
import {formatAddressContains} from 'App/formComponents/formSections/tenderRequest/utils';

//frontend matching only if needed. preferable to use useGetApplicableContracts (backend matching)
export const useGetContracts = ({
  options,
  isBulkShipments,
  stopAddresses,
  equipment,
  modes,
  status,
  searchText
}: {
  options?: UseQueryOptions<PaginatedContract>;
  isBulkShipments?: boolean;
  stopAddresses?: Address[];
  equipment?: EquipmentTypes[] | undefined;
  modes?: ApplicableContractByValueRequestModeEnum[] | undefined;
  status?: string;
  searchText?: string;
}) => {
  const queryParams = {
    q: searchText,
    ...({modes} || null),
    ...(equipment && {equipmentTypes: equipment}),
    ...({status} || null)
  };

  const {enabled, ...restOptions} = options || {};
  const [origin, ...additionalStops] = stopAddresses || [];
  const destination = additionalStops.pop();

  //we need one query that gets the most specific contracts and another to get state matching results
  const [contractsQuery, contractsQueryByState] = useQueries({
    queries: [
      {
        queryKey: [CONTRACTS_QUERY_KEY, queryParams],
        queryFn: () =>
          getContracts({
            // limit contract call to only show contracts that match the shipment's mode and equipment, origin and destination
            ...queryParams,
            originsContains: formatAddressContains(origin),
            destinationsContains: formatAddressContains(destination)
          }),
        ...options
      },
      {
        queryKey: [CONTRACTS_QUERY_KEY, queryParams, origin?.state_province, destination?.state_province],
        queryFn: () =>
          getContracts({
            // a second query is needed to get all contracts that match on state
            ...queryParams,
            originsStateProvince: origin?.state_province ? [origin.state_province] : [],
            destinationsStateProvince: destination?.state_province ? [destination.state_province] : []
          }),
        enabled: (enabled ?? true) && (!!origin?.state_province || !!destination?.state_province),
        ...restOptions
      }
    ]
  });

  //state match is broad and we need to filter to only addresses that have state only
  const filteredContractsByState = contractsQueryByState.data?.results?.filter((contract) =>
    [contract.origins, contract.destinations].every((location, index) =>
      location?.some(
        (location) =>
          ((location.address_1 === [origin, destination][index]?.address_1 &&
            location.postal_code === [origin, destination][index]?.postal_code) ||
            (!location.address_1 &&
              (!(location.city && location.postal_code) ||
                (!location.postal_code && location.city === [origin, destination][index]?.city) ||
                location.postal_code === [origin, destination][index]?.postal_code))) &&
          location.state_province === [origin, destination][index]?.state_province
      )
    )
  );

  const combinedContractsResults = [
    ...uniqBy(
      [
        ...(contractsQuery.data?.results ? contractsQuery.data?.results : []),
        ...(filteredContractsByState ? filteredContractsByState : [])
      ],
      (contract) => contract.id
    ).filter((contract) => {
      // if user is attempting to BULK tender to carrier, we should exclude all contracts with additional stops specified
      if (isBulkShipments) {
        return !contract?.additional_stops?.length;
      }
      // if no additional stops specified, we should show to user
      if (!contract?.additional_stops?.length || !additionalStops) {
        return true;
      }
      // if additional stops are specified on the contract, we should only show the contract if the additionalStops match exactly
      // first check they are at least the same length
      if (contract.additional_stops.length !== additionalStops?.length) {
        return false;
      }
      // sort stops by order_index
      const sortedContractAdditionalStops = [...contract.additional_stops].sort((a, b) => {
        // there is currently an SDK error that doesn not include order_index in the additional_stops of the contract
        if (
          !('order_index' in a) ||
          !('order_index' in b) ||
          typeof a.order_index !== 'number' ||
          typeof b.order_index !== 'number'
        ) {
          return 1;
        }
        if (a.order_index < b.order_index) {
          return -1;
        }
        if (a.order_index > b.order_index) {
          return 1;
        }
        return 0;
      });
      for (let i = 0; i < additionalStops.length; i++) {
        // check if each additional stop's formatted address exists in the same index after sorting by order_index
        if (additionalStops[i].formatted_address !== sortedContractAdditionalStops[i].formatted_address) {
          return false;
        }
      }
      return true;
    })
  ];

  return {combinedContractsResults, contractsQuery, contractsQueryByState};
};
